import {
  TRAINER_CHECKLIST_ITEMS,
  createEmptyTrainerChecklistScores,
  type TrainerChecklistComments,
  type TrainerChecklistScores,
} from '@/lib/trainerChecklist'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'

export const TRAINER_CHECKLIST_DRAFT_KEY = 'trainer-checklist-draft-v2'

/**
 * Ключ черновика. Новый отсмотр пишется в общий слот, правка существующего —
 * в собственный слот `…-review-<id>`.
 *
 * Слоты раздельные, а не один: правка старого отсмотра не должна затирать
 * начатый и ещё не сохранённый новый отсмотр (и наоборот). До 21.08.2026
 * автосейв при `activeReviewId` был просто выключен, то есть 8–15 минут
 * правки не защищало ничего — ни черновик, ни перехват ухода.
 */
export function trainerChecklistDraftKey(reviewId?: string | null): string {
  return reviewId ? `${TRAINER_CHECKLIST_DRAFT_KEY}-review-${reviewId}` : TRAINER_CHECKLIST_DRAFT_KEY
}
const TRAINER_CHECKLIST_DRAFT_SCHEMA_VERSION = 4
export const MIN_REVIEW_PHOTOS = 1
export const TRAINER_REVIEW_MINUTE_OPTIONS = ['00', '15', '30', '45'] as const
export const TRAINER_REVIEW_HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'))
export const TRAINER_REVIEW_OTHER_DIRECTION_ID = '__other__'

export interface TrainerChecklistMeta {
  studioId: string
  city: string
  studioAddress: string
  reviewDate: string
  trainingDateTime: string
  directionId: string
  customDirectionName: string
  coachName: string
}

export interface TrainerChecklistPhoto {
  id: string
  name: string
  previewUrl: string
  file?: File
  photoId?: number
  photoKey?: string
  reviewScoreId?: number
  storageBucket?: string
  storagePath?: string
  mimeType?: string
  sizeBytes?: number
  widthPx?: number
  heightPx?: number
}

export type TrainerChecklistCriterionPhotos = Record<string, TrainerChecklistPhoto[]>

export interface TrainerChecklistDraft {
  meta: TrainerChecklistMeta
  scores: TrainerChecklistScores
  comments: TrainerChecklistComments
  generalComment: string
  generalPhotos: TrainerChecklistPhoto[]
  criterionPhotos: TrainerChecklistCriterionPhotos
  hiddenCriterionRows: Record<string, boolean>
  scoresSchemaVersion?: number
}

export const emptyTrainerChecklistMeta: TrainerChecklistMeta = {
  studioId: '',
  city: '',
  studioAddress: '',
  reviewDate: new Date().toISOString().slice(0, 10),
  trainingDateTime: '',
  directionId: '',
  customDirectionName: '',
  coachName: '',
}

export function createEmptyTrainerChecklistComments(): TrainerChecklistComments {
  return Object.fromEntries(TRAINER_CHECKLIST_ITEMS.map(item => [item.id, '']))
}

export function createEmptyTrainerChecklistCriterionPhotos(): TrainerChecklistCriterionPhotos {
  return Object.fromEntries(TRAINER_CHECKLIST_ITEMS.map(item => [item.id, []]))
}

export function createEmptyTrainerChecklistDraft(): TrainerChecklistDraft {
  return {
    meta: emptyTrainerChecklistMeta,
    scores: createEmptyTrainerChecklistScores(),
    comments: createEmptyTrainerChecklistComments(),
    generalComment: '',
    generalPhotos: [],
    criterionPhotos: createEmptyTrainerChecklistCriterionPhotos(),
    hiddenCriterionRows: {},
    scoresSchemaVersion: TRAINER_CHECKLIST_DRAFT_SCHEMA_VERSION,
  }
}

function normalizeTrainerChecklistScores(
  scores: Partial<TrainerChecklistScores> | undefined,
  shouldMigrateLegacyZeros: boolean
): TrainerChecklistScores {
  const emptyScores = createEmptyTrainerChecklistScores()

  return Object.fromEntries(TRAINER_CHECKLIST_ITEMS.map(item => {
    const score = scores?.[item.id] ?? emptyScores[item.id]
    return [item.id, shouldMigrateLegacyZeros && score === 0 ? null : score]
  }))
}

function normalizePhotos(photos: TrainerChecklistPhoto[] | undefined): TrainerChecklistPhoto[] {
  return (photos ?? []).filter(photo => Boolean(photo?.id && photo?.previewUrl))
}

function normalizeCriterionPhotos(
  photos: Partial<TrainerChecklistCriterionPhotos> | undefined
): TrainerChecklistCriterionPhotos {
  const emptyPhotos = createEmptyTrainerChecklistCriterionPhotos()

  return Object.fromEntries(TRAINER_CHECKLIST_ITEMS.map(item => [
    item.id,
    normalizePhotos(photos?.[item.id] ?? emptyPhotos[item.id]),
  ]))
}

export function normalizeTrainerChecklistDraft(
  value?: (Partial<TrainerChecklistDraft> & { photos?: TrainerChecklistPhoto[] }) | null,
  options: { migrateLegacyZeroScores?: boolean } = {}
): TrainerChecklistDraft {
  const shouldMigrateLegacyZeros = Boolean(
    options.migrateLegacyZeroScores
    && value?.scores
    && value.scoresSchemaVersion !== TRAINER_CHECKLIST_DRAFT_SCHEMA_VERSION
  )

  return {
    meta: { ...emptyTrainerChecklistMeta, ...value?.meta },
    scores: normalizeTrainerChecklistScores(value?.scores, shouldMigrateLegacyZeros),
    comments: { ...createEmptyTrainerChecklistComments(), ...value?.comments },
    generalComment: value?.generalComment ?? '',
    generalPhotos: normalizePhotos(value?.generalPhotos ?? value?.photos),
    criterionPhotos: normalizeCriterionPhotos(value?.criterionPhotos),
    hiddenCriterionRows: { ...(value?.hiddenCriterionRows ?? {}) },
    scoresSchemaVersion: TRAINER_CHECKLIST_DRAFT_SCHEMA_VERSION,
  }
}

export function readTrainerChecklistDraft(reviewId?: string | null): TrainerChecklistDraft {
  try {
    const rawDraft = localStorage.getItem(trainerChecklistDraftKey(reviewId))
    return normalizeTrainerChecklistDraft(
      rawDraft ? JSON.parse(rawDraft) as Partial<TrainerChecklistDraft> : null,
      { migrateLegacyZeroScores: true }
    )
  } catch {
    return createEmptyTrainerChecklistDraft()
  }
}

export function writeTrainerChecklistDraft(draft: TrainerChecklistDraft, reviewId?: string | null) {
  const persistPhotos = (photos: TrainerChecklistPhoto[]) => photos
    .filter(photo => !photo.file && photo.previewUrl)
    .map(photo => ({
      id: photo.id,
      name: photo.name,
      previewUrl: photo.previewUrl,
      photoId: photo.photoId,
      photoKey: photo.photoKey,
      reviewScoreId: photo.reviewScoreId,
      storageBucket: photo.storageBucket,
      storagePath: photo.storagePath,
      mimeType: photo.mimeType,
      sizeBytes: photo.sizeBytes,
      widthPx: photo.widthPx,
      heightPx: photo.heightPx,
    }))

  const persistedCriterionPhotos = Object.fromEntries(
    Object.entries(draft.criterionPhotos).map(([criterionCode, photos]) => [
      criterionCode,
      persistPhotos(photos),
    ])
  )

  localStorage.setItem(trainerChecklistDraftKey(reviewId), JSON.stringify({
    ...draft,
    generalPhotos: persistPhotos(draft.generalPhotos),
    criterionPhotos: persistedCriterionPhotos,
    scoresSchemaVersion: TRAINER_CHECKLIST_DRAFT_SCHEMA_VERSION,
  }))
}

export function clearTrainerChecklistDraft(reviewId?: string | null) {
  localStorage.removeItem(trainerChecklistDraftKey(reviewId))
}

export function hasTrainerChecklistDraft(reviewId?: string | null): boolean {
  try {
    const rawDraft = localStorage.getItem(trainerChecklistDraftKey(reviewId))
    if (!rawDraft) return false
    const draft = normalizeTrainerChecklistDraft(JSON.parse(rawDraft) as Partial<TrainerChecklistDraft>)
    return Boolean(
      draft.meta.studioId
      || draft.meta.coachName.trim()
      || draft.meta.directionId
      || draft.generalComment.trim()
      || draft.generalPhotos.length > 0
      || Object.values(draft.criterionPhotos).some(photos => photos.length > 0)
      || Object.values(draft.comments).some(value => value.trim())
      || Object.values(draft.scores).some(value => value != null)
      || Object.values(draft.hiddenCriterionRows).some(Boolean)
    )
  } catch {
    return false
  }
}

export function createTrainerChecklistReviewLink(reviewId: string): string {
  return `${pedagogyPaths.reviewNew}?reviewId=${encodeURIComponent(reviewId)}`
}

export function createTrainerChecklistReportLink(reviewId: string): string {
  return pedagogyPaths.reviewReport(reviewId)
}

export function buildTrainerChecklistReportUrl(reviewId: string, returnTo?: string): string {
  const reportUrl = createTrainerChecklistReportLink(reviewId)
  if (!returnTo) return reportUrl
  return `${reportUrl}?returnTo=${encodeURIComponent(returnTo)}`
}

export function openTrainerChecklistReport(reviewId: string, returnTo?: string): boolean {
  const popup = window.open(buildTrainerChecklistReportUrl(reviewId, returnTo), '_blank')
  if (!popup) return false
  return true
}

export function createTrainerChecklistDraftLink(): string {
  return `${pedagogyPaths.reviewNew}?draft=1`
}

export function getTrainerChecklistMonthLabel(dateString: string): string {
  if (!dateString) return 'Без даты'

  const date = new Date(`${dateString}T00:00:00`)
  if (Number.isNaN(date.getTime())) return 'Без даты'

  return date.toLocaleDateString('ru-RU', {
    month: 'long',
  }).replace(/^./, first => first.toUpperCase())
}

export function formatTrainerReviewDate(dateString: string): string {
  if (!dateString) return ''

  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return dateString

  return `${match[3]}.${match[2]}.${match[1]}`
}

export function parseTrainerReviewDate(value: string): string | null {
  const match = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
  if (!match) return null

  const [, day, month, year] = match
  const parsed = new Date(`${year}-${month}-${day}T00:00:00`)
  if (
    Number.isNaN(parsed.getTime())
    || parsed.getFullYear() !== Number(year)
    || parsed.getMonth() + 1 !== Number(month)
    || parsed.getDate() !== Number(day)
  ) {
    return null
  }

  return `${year}-${month}-${day}`
}

export function maskTrainerReviewDate(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  const parts = [
    digits.slice(0, 2),
    digits.slice(2, 4),
    digits.slice(4, 8),
  ].filter(Boolean)

  return parts.join('.')
}

export function splitTrainerReviewDateTime(value: string): { date: string; hour: string; minute: string } {
  const match = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/)
  if (!match) return { date: '', hour: '00', minute: '00' }

  const minute = TRAINER_REVIEW_MINUTE_OPTIONS.includes(match[3] as typeof TRAINER_REVIEW_MINUTE_OPTIONS[number])
    ? match[3]
    : '00'

  return {
    date: match[1],
    hour: match[2],
    minute,
  }
}

export function composeTrainerReviewDateTime(date: string, hour: string, minute: string): string {
  if (!date) return ''
  const safeHour = TRAINER_REVIEW_HOUR_OPTIONS.includes(hour) ? hour : '00'
  const safeMinute = TRAINER_REVIEW_MINUTE_OPTIONS.includes(minute as typeof TRAINER_REVIEW_MINUTE_OPTIONS[number])
    ? minute
    : '00'

  return `${date}T${safeHour}:${safeMinute}`
}

export function formatTrainerReviewDateTime(value: string): string {
  const parts = splitTrainerReviewDateTime(value)
  if (!parts.date) return ''

  return `${formatTrainerReviewDate(parts.date)} ${parts.hour}:${parts.minute}`
}
